import { LegalPageLayout } from '../components';

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="March 13, 2026">
      <p>
        Fias Inc. (&quot;Fias,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates
        the Fias platform. This Privacy Policy explains how we collect, use, disclose, and protect
        your personal information when you use our Service.
      </p>
      <p>
        By using the Service, you agree to the collection and use of information in accordance with
        this Privacy Policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Information You Provide</h3>
      <ul>
        <li>
          <strong>Account information:</strong> username, email address, and password when you
          create an account
        </li>
        <li>
          <strong>Content:</strong> messages, conversations, Arches, plugins, and other content you
          create or submit through the Service
        </li>
        <li>
          <strong>Communications:</strong> messages you send through chat channels, direct messages,
          and guilds
        </li>
        <li>
          <strong>Payment information:</strong> credit purchase history and transaction records (we
          do not store payment card details directly)
        </li>
        <li>
          <strong>Support requests:</strong> information you provide when contacting us for support
        </li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <ul>
        <li>
          <strong>Usage data:</strong> pages visited, features used, interactions with the Service,
          and timestamps
        </li>
        <li>
          <strong>Device information:</strong> browser type, operating system, and device
          identifiers
        </li>
        <li>
          <strong>Log data:</strong> IP address, access times, and error logs
        </li>
        <li>
          <strong>AI usage data:</strong> AI model provider, model name, token counts, and
          performance metrics associated with your use of AI-powered features
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, operate, and maintain the Service</li>
        <li>Process transactions and manage your account</li>
        <li>
          Send you service-related communications (e.g., account verification, security alerts)
        </li>
        <li>Enforce our Terms of Service and Acceptable Use Policy</li>
        <li>Respond to your requests and provide support</li>
        <li>Monitor and analyze usage patterns to improve the Service</li>
        <li>Detect, prevent, and address technical issues, fraud, and abuse</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        <strong>We do not use your content, conversations, or inputs to train AI models.</strong>
      </p>

      <h2>3. How We Share Your Information</h2>
      <p>We may share your information in the following circumstances:</p>
      <ul>
        <li>
          <strong>Third-party AI providers:</strong> When you use AI-powered features, your inputs
          are sent to third-party AI providers (such as OpenAI, Anthropic, and Google) for
          processing. These providers process your data according to their own privacy policies.
        </li>
        <li>
          <strong>Service providers:</strong> We use third-party services to help operate the
          Service, including cloud hosting (Amazon Web Services), authentication, and analytics.
          These providers access your information only as necessary to perform their services.
        </li>
        <li>
          <strong>Legal requirements:</strong> We may disclose your information if required by law,
          court order, or governmental regulation, or if we believe disclosure is necessary to
          protect our rights, your safety, or the safety of others.
        </li>
        <li>
          <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of
          assets, your information may be transferred to the acquiring entity.
        </li>
        <li>
          <strong>With your consent:</strong> We may share your information for purposes not
          described here with your explicit consent.
        </li>
      </ul>
      <p>We do not sell your personal information to third parties.</p>

      <h2>4. Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is active or as needed to
        provide the Service. When you delete content or close your account, we will delete or
        anonymize your information within a reasonable timeframe, except where we are required to
        retain it for legal, regulatory, or legitimate business purposes (such as resolving disputes
        or enforcing our agreements).
      </p>
      <p>Shared conversation links automatically expire 30 days after creation.</p>

      <h2>5. Your Rights and Choices</h2>
      <p>Depending on your location, you may have the following rights:</p>
      <ul>
        <li>
          <strong>Access:</strong> Request a copy of the personal information we hold about you
        </li>
        <li>
          <strong>Deletion:</strong> Request deletion of your personal information
        </li>
        <li>
          <strong>Correction:</strong> Request correction of inaccurate personal information
        </li>
        <li>
          <strong>Export:</strong> Export your conversation data in JSON or Markdown format through
          the Service
        </li>
        <li>
          <strong>Account closure:</strong> Close your account at any time through your account
          settings
        </li>
      </ul>
      <p>
        To exercise these rights, contact us at <a href="mailto:privacy@fias.io">privacy@fias.io</a>
        . We will respond to your request within 30 days, or as required by applicable law.
      </p>

      <h3>California Residents</h3>
      <p>
        If you are a California resident, you have additional rights under the California Consumer
        Privacy Act (CCPA), including the right to know what personal information we collect and how
        it is used, the right to request deletion, and the right to opt out of the sale of personal
        information. We do not sell personal information. To exercise your rights, contact us at{' '}
        <a href="mailto:privacy@fias.io">privacy@fias.io</a>.
      </p>

      <h2>6. Data Security</h2>
      <p>
        We implement reasonable technical and organizational measures to protect your personal
        information, including encryption in transit and at rest, access controls, and regular
        security assessments. However, no method of transmission over the Internet or electronic
        storage is completely secure, and we cannot guarantee absolute security.
      </p>

      <h2>7. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in the United States, where our servers
        are located. If you are accessing the Service from outside the United States, please be
        aware that your information may be subject to U.S. data protection laws, which may differ
        from those in your country.
      </p>

      <h2>8. Children&apos;s Privacy</h2>
      <p>
        The Service is not intended for anyone under 18 years of age. We do not knowingly collect
        personal information from children under 18. If we learn that we have collected information
        from a child under 18, we will take steps to delete that information promptly.
      </p>

      <h2>9. Third-Party Links and Services</h2>
      <p>
        The Service may contain links to third-party websites or integrate with third-party
        services. This Privacy Policy does not apply to those third parties. We encourage you to
        review the privacy policies of any third-party services you use.
      </p>

      <h2>10. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If we make material changes, we will
        notify you by posting the updated policy on the Service with a new &quot;Last Updated&quot;
        date. Your continued use of the Service after the changes take effect constitutes your
        acceptance of the revised policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or our data practices, please contact us at:
      </p>
      <p>
        Fias Inc.
        <br />
        Email: <a href="mailto:privacy@fias.io">privacy@fias.io</a>
      </p>
    </LegalPageLayout>
  );
}
